import { useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { UOM, UOMFilters, UOMSortOptions, CreateUOMInput, UpdateUOMInput } from '@/types/uom';

/**
 * Hook for managing UOM (Unit of Measurement) data
 */
export const useUOM = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Get UOMs with optional filtering, sorting, and pagination
   */
  const getUOMs = async (
    filters: UOMFilters = {},
    sortOptions: UOMSortOptions = { field: 'code', direction: 'asc' },
    limit?: number,
    offset?: number
  ) => {
    // Don't set loading state here to prevent re-renders
    // setLoading(true);
    // setError(null);

    try {
      let query = supabase.from('uoms').select('*');

      // Apply filters
      if (filters.search) {
        query = query.or(`code.ilike.%${filters.search}%,name.ilike.%${filters.search}%`);
      }

      if (filters.category) {
        query = query.eq('category', filters.category);
      }

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.is_base_uom !== undefined) {
        query = query.eq('is_base_uom', filters.is_base_uom);
      }

      // Apply sorting
      query = query.order(sortOptions.field, {
        ascending: sortOptions.direction === 'asc',
      });

      // Apply pagination
      if (limit !== undefined) {
        query = query.limit(limit);
      }

      if (offset !== undefined) {
        query = query.range(offset, offset + (limit || 10) - 1);
      }

      // Execute query
      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      return { data: data as UOM[] };
    } catch (err) {
      const error = err as Error;
      // setError(error);
      return { data: [], error };
    } finally {
      // Don't set loading state here to prevent re-renders
      // setLoading(false);
    }
  };

  /**
   * Get a single UOM by ID
   */
  const getUOMByID = async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('uoms')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      return { data: data as UOM };
    } catch (err) {
      const error = err as Error;
      setError(error);
      return { data: null };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get the base UOM for a specific category
   */
  const getBaseUOMForCategory = async (category: string) => {
    try {
      const { data, error } = await supabase
        .from('uoms')
        .select('*')
        .eq('category', category)
        .eq('is_base_uom', true)
        .single();

      if (error) throw error;

      return data as UOM;
    } catch (err) {
      return null;
    }
  };

  /**
   * Create a new UOM
   */
  const createUOM = async (uomData: CreateUOMInput) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('uoms')
        .insert([uomData])
        .select()
        .single();

      if (error) throw error;

      return data as UOM;
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update an existing UOM
   */
  const updateUOM = async (uomData: UpdateUOMInput) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('uoms')
        .update(uomData)
        .eq('id', uomData.id)
        .select()
        .single();

      if (error) throw error;

      return data as UOM;
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete a UOM by ID
   */
  const deleteUOM = async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('uoms')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { success: true };
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Convert a value from one UOM to another
   */
  const convertValue = async (fromUomId: string, toUomId: string, value: number) => {
    try {
      // Get the UOMs
      const { data: fromUom, error: fromError } = await supabase
        .from('uoms')
        .select('*')
        .eq('id', fromUomId)
        .single();
        
      const { data: toUom, error: toError } = await supabase
        .from('uoms')
        .select('*')
        .eq('id', toUomId)
        .single();
        
      if (fromError || toError || !fromUom || !toUom) {
        throw new Error('Failed to retrieve UOMs');
      }
      
      // If the UOMs are the same, no conversion needed
      if (fromUomId === toUomId) {
        return value;
      }
      
      // If both are base UOMs but different categories, conversion not possible
      if (fromUom.is_base_uom && toUom.is_base_uom && fromUom.category !== toUom.category) {
        throw new Error('Cannot convert between different UOM categories');
      }
      
      // If they are in the same category, we can convert
      if (fromUom.category === toUom.category) {
        // Convert from source UOM to base UOM
        let baseValue = value;
        if (!fromUom.is_base_uom && fromUom.conversion_factor) {
          baseValue = value * fromUom.conversion_factor;
        }
        
        // Convert from base UOM to target UOM
        if (!toUom.is_base_uom && toUom.conversion_factor) {
          return baseValue / toUom.conversion_factor;
        }
        
        return baseValue;
      }
      
      throw new Error('Conversion not possible');
    } catch (error) {
      throw error;
    }
  };

  return {
    loading,
    error,
    getUOMs,
    getUOMByID,
    getBaseUOMForCategory,
    createUOM,
    updateUOM,
    deleteUOM,
    convertValue,
  };
};

export default useUOM;
